import type { SvgIconComponent } from '@mui/icons-material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import type { AiAwarenessTrainingStatus } from '../config/aiAwarenessTrainingConfig';
import './AiAwarenessSlide.css';

type SlideStatus = Extract<
  AiAwarenessTrainingStatus,
  'Introduction' | 'AI at Work' | 'Data Privacy' | 'Misinformation' | 'Security & Access' | 'Accountability' | 'The Upside' | 'Key Takeaways'
>;

interface AiAwarenessSlideProps {
  status: SlideStatus;
}

interface RiskSlideDefinition {
  page: string;
  section: string;
  title: string;
  icon: SvgIconComponent;
  examples: readonly string[];
  practices: readonly string[];
  decoration: 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right';
}

const RISK_SLIDES: Record<'Data Privacy' | 'Misinformation' | 'Security & Access' | 'Accountability', RiskSlideDefinition> = {
  'Data Privacy': {
    page: '03',
    section: 'Compliance & Data Privacy',
    title: 'What Not to Paste Into a Chatbot',
    icon: LockOutlinedIcon,
    decoration: 'top-right',
    examples: [
      "Copying a client's medical or financial info into an AI tool that isn't approved for PHI, just to help draft an email.",
      'Uploading internal reports or documents to an AI tool for “summarization.”',
      "Either one can become a HIPAA breach or a broken confidentiality agreement, and whatever you typed may sit on a vendor's server for good.",
    ],
    practices: [
      'Know the tiers: tools approved for PHI, tools approved for general work only, and tools not approved at all.',
      'No names, no medical details, no financial data. Ever.',
      'Not sure? Ask HR or IT first. It takes two minutes.',
    ],
  },
  Misinformation: {
    page: '04',
    section: 'Misinformation',
    title: "AI Sounds Sure Even When It's Wrong",
    icon: WarningAmberRoundedIcon,
    decoration: 'top-left',
    examples: [
      'AI will invent a policy, a number, or a citation and present it exactly like fact.',
      'Drafting a policy summary or a compliance answer and sending it without checking.',
      'Passing AI text to a client or a family member without reading it closely.',
    ],
    practices: [
      'Treat AI like a brainstorming assistant, not a truth source.',
      'Check anything factual against the real source.',
      'Nothing leaves your desk unread.',
    ],
  },
  'Security & Access': {
    page: '05',
    section: 'Security & Access',
    title: 'Accounts, Logins, and Where Files Land',
    icon: KeyRoundedIcon,
    decoration: 'bottom-right',
    examples: [
      'Work done on a personal account sits outside everything IT can protect or recover.',
      'Using your personal ChatGPT account for work materials.',
      'Saving AI output to a personal drive, phone, or notes app.',
    ],
    practices: [
      'Company accounts, on company-approved platforms.',
      'Log out after each session.',
      'Keep your personal accounts out of work entirely.',
    ],
  },
  Accountability: {
    page: '06',
    section: 'Accountability',
    title: 'Your Name Is Still On It',
    icon: PersonOutlineRoundedIcon,
    decoration: 'bottom-left',
    examples: [
      'When something goes out wrong, “the AI wrote it” is not an answer anyone accepts.',
      'An employee copies an AI-generated report without review, and it contains errors.',
      'A team assumes AI “checked the math,” but it didn’t.',
    ],
    practices: [
      'You own the work whether or not AI helped write it.',
      'Document when and how you use AI tools.',
      'Loop in your supervisor before AI touches a decision about a person.',
    ],
  },
};

const INTRO_CARDS = [
  { icon: LockOutlinedIcon, title: 'Know What Stays In-House', body: 'What’s safe to type into an AI tool, and what isn’t.', tone: 'cyan' },
  { icon: WarningAmberRoundedIcon, title: 'Catch What AI Gets Wrong', body: 'How to tell when an answer needs a second look.', tone: 'purple' },
  { icon: SecurityOutlinedIcon, title: 'Stay Inside the Rules', body: 'Where HIPAA, privacy law, and company policy draw the line.', tone: 'cyan' },
  { icon: TrendingUpRoundedIcon, title: 'Get Real Time Back', body: 'Where these tools really help, without the exposure.', tone: 'purple' },
] as const;

function SlideNumber({ children }: { children: string }) {
  return <span className="ai-slide__number" aria-hidden="true">{children}</span>;
}

function DecorativeBlob({ position, blue = false }: { position: RiskSlideDefinition['decoration']; blue?: boolean }) {
  return <span className={`ai-slide__blob ai-slide__blob--${position}${blue ? ' ai-slide__blob--blue' : ''}`} aria-hidden="true" />;
}

function CoverSlide() {
  return (
    <article className="ai-slide ai-slide--cover" aria-labelledby="ai-slide-title-01">
      <div className="ai-slide-cover__glow" aria-hidden="true" />
      <div className="ai-slide-cover__orb" aria-hidden="true"><span /></div>
      <header className="ai-slide-cover__brand" aria-label="Evo Care Group HR Training">
        <div className="ai-slide-cover__logo"><span className="ai-slide-cover__mark" aria-hidden="true">{Array.from({ length: 8 }, (_, index) => <i key={index} />)}</span><strong>evo</strong></div>
        <div className="ai-slide-cover__eyebrow">Care Group <b>•</b> HR Training</div>
      </header>
      <div className="ai-slide-cover__content">
        <h2 id="ai-slide-title-01"><strong>AI</strong><span>Awareness</span></h2>
        <p className="ai-slide-cover__subtitle">Responsible Use Training</p>
        <div className="ai-slide-cover__promise"><span>Use AI with confidence.</span><span>Protect data. Verify everything.</span></div>
      </div>
      <SlideNumber>01</SlideNumber>
    </article>
  );
}

function IntroductionSlide() {
  return (
    <article className="ai-slide ai-slide--introduction" aria-labelledby="ai-slide-title-02">
      <DecorativeBlob position="top-right" />
      <header className="ai-slide__header">
        <p className="ai-slide__section">Introduction</p>
        <h2 id="ai-slide-title-02" className="ai-slide__title">AI Is Already Part of the Work</h2>
        <p className="ai-slide__lede">ChatGPT, Copilot and tools like them save real time. They can also put client or employee data somewhere we can’t take it back. Everyone here touches that data, so this one is for all of us.</p>
      </header>
      <div className="ai-slide-intro__grid">
        {INTRO_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <section className="ai-slide-intro__card" key={card.title}>
              <span className={`ai-slide__icon ai-slide__icon--${card.tone}`} aria-hidden="true"><Icon /></span>
              <div><h3>{card.title}</h3><p>{card.body}</p></div>
            </section>
          );
        })}
      </div>
      <SlideNumber>02</SlideNumber>
    </article>
  );
}

function BulletList({ items }: { items: readonly string[] }) {
  return <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>;
}

function RiskSlide({ definition }: { definition: RiskSlideDefinition }) {
  const Icon = definition.icon;
  const titleId = `ai-slide-title-${definition.page}`;
  return (
    <article className="ai-slide ai-slide--risk" aria-labelledby={titleId}>
      <DecorativeBlob position={definition.decoration} />
      <header className="ai-slide__header">
        <p className="ai-slide__section">{definition.section}</p>
        <h2 id={titleId} className="ai-slide__title">{definition.title}</h2>
      </header>
      <div className="ai-slide__kicker ai-slide__kicker--risk"><span className="ai-slide__icon ai-slide__icon--coral" aria-hidden="true"><Icon /></span><h3>The Risk</h3></div>
      <div className="ai-slide-risk__grid">
        <section className="ai-slide__panel ai-slide__panel--examples"><h3>Examples</h3><BulletList items={definition.examples} /></section>
        <section className="ai-slide__panel ai-slide__panel--practice"><div className="ai-slide__panel-title"><span className="ai-slide__check" aria-hidden="true"><TaskAltRoundedIcon /></span><h3>Best Practice</h3></div><BulletList items={definition.practices} /></section>
      </div>
      <SlideNumber>{definition.page}</SlideNumber>
    </article>
  );
}

function OpportunitySlide() {
  return (
    <article className="ai-slide ai-slide--opportunity" aria-labelledby="ai-slide-title-07">
      <DecorativeBlob position="top-right" />
      <header className="ai-slide__header">
        <p className="ai-slide__section ai-slide__section--lavender">The Upside</p>
        <h2 id="ai-slide-title-07" className="ai-slide__title">Where This Actually Pays Off</h2>
      </header>
      <div className="ai-slide__kicker ai-slide__kicker--opportunity"><span className="ai-slide__icon ai-slide__icon--purple" aria-hidden="true"><TrendingUpRoundedIcon /></span><h3>The Opportunity</h3></div>
      <div className="ai-slide-risk__grid">
        <section className="ai-slide__panel ai-slide__panel--examples"><h3>Examples</h3><BulletList items={[
          'Used well, these tools take the grind out of drafting, summarizing, and cleanup work.',
          'Brainstorming, first drafts, turning a forty-message thread into three bullets.',
          'Knowing where the tool is strong and where it falls apart.',
        ]} /></section>
        <section className="ai-slide__panel ai-slide__panel--practice ai-slide__panel--purple"><div className="ai-slide__panel-title"><span className="ai-slide__check" aria-hidden="true"><TaskAltRoundedIcon /></span><h3>Best Practice</h3></div><BulletList items={[
          'Try the approved tools. Really try them.',
          'Bring what works back to your team.',
          'The people who figure this out first end up leading it.',
        ]} /></section>
      </div>
      <SlideNumber>07</SlideNumber>
    </article>
  );
}

const TAKEAWAYS = [
  'Approved tools for approved use and company accounts only. Never a personal login.',
  'Client, employee, and financial information stays out of AI tools unless approved for such use.',
  'Read and verify anything AI writes before it goes out. Your name is on it.',
  'Not sure whether something is safe? Ask HR or IT before you paste.',
] as const;

function TakeawaysSlide() {
  return (
    <article className="ai-slide ai-slide--takeaways" aria-labelledby="ai-slide-title-08">
      <DecorativeBlob position="top-right" blue />
      <DecorativeBlob position="bottom-left" />
      <span className="ai-slide__icon ai-slide__icon--cyan ai-slide-takeaways__hero-icon" aria-hidden="true"><ChatBubbleOutlineRoundedIcon /></span>
      <header className="ai-slide__header">
        <h2 id="ai-slide-title-08" className="ai-slide__title">Nice Work. Here’s the Short Version.</h2>
        <p className="ai-slide__lede ai-slide__lede--italic">The point was never to keep you away from these tools. It’s to make sure the way we use them holds up.</p>
      </header>
      <section className="ai-slide-takeaways__list"><h3>The Four Things That Matter</h3><ul>{TAKEAWAYS.map((item) => <li key={item}><span aria-hidden="true"><CheckRoundedIcon /></span>{item}</li>)}</ul></section>
      <footer className="ai-slide-takeaways__footer"><p>Do these four and we get everything these tools are good for without putting our data at risk.</p><em>Next step: the short quiz and the acknowledgment form.</em></footer>
      <SlideNumber>08</SlideNumber>
    </article>
  );
}

export default function AiAwarenessSlide({ status }: AiAwarenessSlideProps) {
  if (status === 'Introduction') return <CoverSlide />;
  if (status === 'AI at Work') return <IntroductionSlide />;
  if (status === 'The Upside') return <OpportunitySlide />;
  if (status === 'Key Takeaways') return <TakeawaysSlide />;
  return <RiskSlide definition={RISK_SLIDES[status]} />;
}
